import { useState, useRef } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { Label } from "@/shared/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/shared/components/ui/select";
import { MessageSquarePlus, Send, Loader2, Paperclip, X } from "lucide-react";
import { supabase } from "@/shared/integrations/supabase/client";
import { R2_MAX_FILE_SIZE_BYTES } from "@/shared/lib/uploadConstants";
import { useAuth } from "@/shared/hooks/useAuth";
import { useToast } from "@/shared/hooks/use-toast";
import { Badge } from "@/shared/components/ui/badge";

const MAX_FILE_SIZE = R2_MAX_FILE_SIZE_BYTES; // 5 TB — sem limite prático; roteados para R2
const ALLOWED_FEEDBACK_EXTENSIONS = ["pdf", "docx", "dotx", "png", "jpg", "jpeg", "webp", "txt"];
const ALLOWED_FEEDBACK_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.template",
  "image/png",
  "image/jpeg",
  "image/webp",
  "text/plain",
];

const getFileExtension = (fileName: string) => fileName.split(".").pop()?.toLowerCase() || "";

const FeedbackDialog = () => {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("sugestao");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (selected.size > MAX_FILE_SIZE) {
      toast({ title: "Arquivo muito grande", description: "O limite máximo é 5 TB.", variant: "destructive" });
      return;
    }

    const ext = getFileExtension(selected.name);
    const isAllowedExt = ALLOWED_FEEDBACK_EXTENSIONS.includes(ext);
    const isAllowedMime = selected.type ? ALLOWED_FEEDBACK_MIME_TYPES.includes(selected.type) : true;
    if (!isAllowedExt || !isAllowedMime) {
      toast({
        title: "Tipo de arquivo não permitido",
        description: "Use PDF, DOCX, DOTX, PNG, JPG, WEBP ou TXT.",
        variant: "destructive",
      });
      return;
    }

    setFile(selected);
  };

  const handleSubmit = async () => {
    if (!subject.trim() || !message.trim() || !user) return;
    setSending(true);

    try {
      let attachmentUrl: string | null = null;

      // Upload file if present
      if (file) {
        const ext = file.name.split(".").pop() || "bin";
        const path = `${user.id}/${Date.now()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from("feedback-attachments")
          .upload(path, file, { contentType: file.type || undefined });
        if (uploadError) throw uploadError;
        attachmentUrl = path;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("nome")
        .eq("user_id", user.id)
        .maybeSingle();

      const { error } = await (supabase.from("user_feedback") as any).insert({
        user_id: user.id,
        user_name: profile?.nome || "Usuário",
        category,
        subject: subject.trim(),
        message: message.trim(),
        attachment_url: attachmentUrl,
      });

      if (error) throw error;

      // A caixa de mensagens do header é preenchida pelo trigger notify_admins_on_user_feedback (cada admin).

      toast({ title: "Feedback enviado!", description: "O administrador receberá sua mensagem." });
      setSubject("");
      setMessage("");
      setCategory("sugestao");
      setFile(null);
      setOpen(false);
    } catch (error: any) {
      toast({ title: "Erro ao enviar feedback", description: error.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5 text-sidebar-foreground hover:text-sidebar-primary hover:bg-sidebar-accent">
          <MessageSquarePlus className="h-4 w-4" />
          <span>Feedback</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>Enviar Feedback</DialogTitle>
          <DialogDescription className="sr-only">Formulário para enviar feedback sobre o sistema</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2 min-w-0">
          <div className="space-y-2 min-w-0">
            <Label htmlFor="feedback-category">Categoria</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="feedback-category" aria-label="Categoria do feedback" className="w-full max-w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bug">🐛 Reportar Bug</SelectItem>
                <SelectItem value="sugestao">💡 Sugestão</SelectItem>
                <SelectItem value="reclamacao">⚠️ Reclamação</SelectItem>
                <SelectItem value="elogio">👏 Elogio</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 min-w-0">
            <Label htmlFor="feedback-subject">Assunto</Label>
            <Input
              id="feedback-subject"
              name="feedback-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Resumo do feedback"
              maxLength={100}
              className="w-full min-w-0"
            />
          </div>
          <div className="space-y-2 min-w-0">
            <Label htmlFor="feedback-message">Mensagem</Label>
            <Textarea
              id="feedback-message"
              name="feedback-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Descreva em detalhes..."
              className="min-h-[100px] max-h-[220px] resize-y w-full min-w-0 box-border"
              maxLength={1000}
            />
          </div>

          {/* File attachment */}
          <div className="space-y-2 min-w-0">
            <Label htmlFor="feedback-attachment">Anexo (opcional)</Label>
            <input
              id="feedback-attachment"
              name="feedback-attachment"
              ref={fileRef}
              type="file"
              accept=".pdf,.docx,.dotx,.png,.jpg,.jpeg,.webp,.txt"
              className="hidden"
              onChange={handleFileChange}
            />
            {file ? (
              <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50 border min-w-0 max-w-full overflow-hidden">
                <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm truncate flex-1 min-w-0">{file.name}</span>
                <Badge variant="outline" className="text-xs shrink-0 tabular-nums">
                  {(file.size / 1024).toFixed(0)} KB
                </Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0"
                  onClick={() => setFile(null)}
                  title="Remover anexo"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => fileRef.current?.click()}
              >
                <Paperclip className="h-3.5 w-3.5" />
                Anexar arquivo
              </Button>
            )}
            <p className="text-xs text-muted-foreground">Máximo 10 MB • PDF, DOCX, DOTX, PNG, JPG, WEBP ou TXT</p>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={sending || !subject.trim() || !message.trim()}
            className="w-full gap-2"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Enviar Feedback
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackDialog;
